import React, { memo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Wallet, ArrowDown, ArrowUp, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { useAuth } from "../../../../hooks";
import { Icons } from "../../../assets/icon";

interface WalletCardData {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  chartData: { value: number }[];
}

const ChipWalletOverview: React.FC = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { userInfo } = useAuth();

  const walletStats: WalletCardData[] = [
    {
      label: "Total Earned",
      value: "$1,240.50",
      icon: <ArrowUp size={22} />,
      color: theme.palette.success.main,
      chartData: [{ value: 200 }, { value: 400 }, { value: 1240 }],
    },
    {
      label: "Total Spent",
      value: "$420.00",
      icon: <ArrowDown size={22} />,
      color: theme.palette.error.main,
      chartData: [{ value: 100 }, { value: 320 }, { value: 420 }],
    },
    {
      label: "TaaS Coins",
      value: userInfo?.available_chips ?? 0,
      icon: <Icons.Coin />,
      color: theme.palette.primary.main,
      chartData: [{ value: 50 }, { value: 200 }, { value: 312 }],
    },
  ];

  // — Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Title row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
        flexWrap="wrap"
        gap={2}
      >
        <Typography
          variant="h6"
          fontWeight={900}
          fontFamily="Lexend"
          sx={{
            color: COLORS.text,
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            "&::after": {
              content: '""',
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: COLORS.red,
              boxShadow: "0 0 12px rgba(229,9,20,0.6)",
              display: "inline-block",
            },
          }}
        >
          Wallet Overview
        </Typography>

        <Button
          onClick={() => navigate("/wallet")}
          variant="text"
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontWeight: 700,
            color: COLORS.red,
            minWidth: "auto",
            px: 0.5,
            alignSelf: isSmall ? "flex-start" : "center",
            "&:hover": { color: COLORS.redHover, textDecoration: "underline" },
          }}
        >
          View Details
        </Button>
      </Stack>

      {/* Cards */}
      <Stack direction="row" spacing={3} mb={3} flexWrap="wrap" useFlexGap>
        {walletStats.map((item, idx) => (
          <Paper
            key={idx}
            elevation={0}
            onClick={() => navigate("/wallet")}
            sx={{
              flex: isSmall
                ? "1 1 100%"
                : idx === 2
                ? "1 1 100%"
                : "1 1 calc(50% - 16px)",
              minWidth: 220,
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              background: `
                radial-gradient(800px 300px at 0% 0%, rgba(229,9,20,0.08), transparent 60%),
                linear-gradient(180deg, #151515, #111111)
              `,
              color: COLORS.text,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transition:
                "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
              border: `1px solid ${COLORS.border}`,
              cursor: "pointer",
              flexDirection: "column",
              textAlign: "left",
              boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: "rgba(255,255,255,0.18)",
                boxShadow:
                  "0 22px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04) inset",
              },
            }}
          >
            <Box
              width="100%"
              mb={1.25}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ gap: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color={COLORS.muted}
                  fontFamily="Lexend"
                  noWrap
                  title={item.label}
                >
                  {item.label}
                </Typography>
                <Typography
                  fontSize={isSmall ? 18 : 20}
                  fontWeight={800}
                  mt={0.5}
                  fontFamily="Lexend"
                  sx={{
                    background:
                      "linear-gradient(90deg, #FFFFFF 0%, rgba(255,255,255,0.85) 60%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textShadow: "0 1px 0 rgba(0,0,0,0.25)",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  color: item.color,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 0,
                }}
              >
                {item.icon}
              </Box>
            </Box>

            {/* Mini chart */}
            <Box width="100%" height={60}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={item.chartData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={item.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      backgroundColor: "#141414",
                      border: `1px solid ${COLORS.border}`,
                      color: COLORS.text,
                    }}
                    wrapperStyle={{ outline: "none" }}
                    cursor={{ strokeDasharray: "3 3" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default memo(ChipWalletOverview);
